import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AlertTriangle, Send, Shield, MapPin, FileText,
  Zap, User, Clock, Camera, Phone, Hash, Upload, X, CheckCircle,
} from 'lucide-react';
import {
  REGIONS, CITIES, INCIDENT_TYPES, SEVERITY_LEVELS, PRECINCTS,
  STATUS_OPTIONS, SHEETS_API_URL, generateIncidentId, generateOfficerId,
} from '../../config/incidents';

const SECTION = ({ title, icon: Icon, children }: any) => (
  <div className="space-y-4">
    <div className="flex items-center gap-2 pb-2 border-b border-blue-900/60">
      <Icon className="w-4 h-4 text-gold-500" />
      <h3 className="text-sm font-semibold text-gold-500 uppercase tracking-wider">{title}</h3>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Field = ({ label, required = true, full = false, children }: any) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-xs font-medium text-blue-300 mb-1.5">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 transition-all";
const selectCls = inputCls;

export const IncidentForm = ({ user, onSubmitted }: { user: any; onSubmitted?: () => void }) => {
  const now = new Date();
  const [loading, setLoading]   = useState(false);
  const [success, setSuccess]   = useState(false);
  const [error, setError]       = useState('');
  const [photo, setPhoto]       = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState('');

  const [form, setForm] = useState({
    // Auto-generated
    incident_id:            generateIncidentId(),
    officer_id:             generateOfficerId(),
    created_at:             now.toISOString().slice(0,10),
    updated_at:             now.toISOString().slice(0,10),
    // Section 1 — Identification
    incident_date:          now.toISOString().slice(0,10),
    incident_time:          now.toTimeString().slice(0,5),
    incident_type:          '',
    severity:               '',
    region:                 '',
    city:                   '',
    location_detail:        '',
    // Section 2 — Reporter
    reported_by:            user?.name || user?.username || '',
    reporter_role:          user?.role || '',
    reporter_phone:         '',
    assigned_precinct:      '',
    assigned_email:         '',
    // Section 3 — Incident details
    description:            '',
    victims_count:          '',
    suspects_count:         '',
    suspects_description:   '',
    weapons_involved:       '',
    property_damage:        '',
    // Section 4 — Response
    immediate_actions:      '',
    units_deployed:         '',
    response_time_minutes:  '',
    backup_requested:       '',
    // Section 5 — Status & approval
    status:                 'Ouvert',
    time_to_complete:       '',
    approved_by:            '',
    // Section 6 — Evidence
    photo_url:              '',
    witness_name:           '',
    witness_contact:        '',
    additional_notes:       '',
  });

  const set = (k: string, v: string) => setForm(f => ({...f, [k]: v}));
  const cities = form.region ? CITIES[form.region] || [] : [];
  const sevConfig = SEVERITY_LEVELS.find(s => s.value === form.severity);

  const handlePhoto = (file: File) => {
    if (file.size > 10 * 1024 * 1024) { alert('Photo max 10MB'); return; }
    setPhoto(file);
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      setPhotoPreview(dataUrl);
      set('photo_url', dataUrl);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.incident_type || !form.severity || !form.region || !form.city || !form.description) {
      setError('Veuillez remplir tous les champs obligatoires (*)'); return;
    }
    setLoading(true); setError('');

    const payload = {
      ...form,
      incident_date: `${form.incident_date} ${form.incident_time}`,
      updated_at: new Date().toISOString().slice(0,10),
    };

    try {
      await fetch(SHEETS_API_URL, {
        method: 'POST', mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      setSuccess(true);
      setTimeout(() => { setSuccess(false); onSubmitted?.(); }, 3500);
    } catch {
      setError('Erreur de transmission. Vérifiez votre connexion et réessayez.');
    }
    setLoading(false);
  };

  if (success) return (
    <div className="flex flex-col items-center justify-center py-20">
      <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mb-5 border-2 border-green-500/30">
        <CheckCircle className="w-10 h-10 text-green-400" />
      </div>
      <h3 className="text-2xl font-bold text-green-400 mb-2">Incident enregistré</h3>
      <p className="text-slate-200 text-sm mb-1">Référence : <span className="font-mono text-white">{form.incident_id}</span></p>
      <p className="text-slate-400 text-xs">Rapport transmis au système de commandement NEXUS360.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto">
      <div className="rounded-xl overflow-hidden border border-blue-900/40 bg-slate-800/80 shadow-xl">

        {/* Form header */}
        <div className="p-5 border-b border-blue-900/60 bg-slate-700/80">
          <div className="flex items-start justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-red-500/10 rounded-xl border border-red-500/20">
                <AlertTriangle className="w-6 h-6 text-red-400" />
              </div>
              <div>
                <h2 className="text-white font-bold text-lg">Déclaration d'incident opérationnel</h2>
                <p className="text-xs text-blue-300">NEXUS360 · Système de Gestion des Incidents · DGSN</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs text-blue-300">Référence</p>
              <p className="font-mono text-gold-500 font-bold">{form.incident_id}</p>
              <p className="text-xs text-slate-400 mt-0.5">{form.created_at}</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">

          {/* ── Section 1: Identification ── */}
          <SECTION title="Identification de l'incident" icon={Hash}>
            <Field label="Date de l'incident">
              <input type="date" value={form.incident_date} onChange={e => set('incident_date', e.target.value)} required className={inputCls}/>
            </Field>
            <Field label="Heure de l'incident">
              <input type="time" value={form.incident_time} onChange={e => set('incident_time', e.target.value)} required className={inputCls}/>
            </Field>
            <Field label="Type d'incident">
              <select value={form.incident_type} onChange={e => set('incident_type', e.target.value)} required className={selectCls}>
                <option value="">Sélectionner...</option>
                {INCIDENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Niveau de sévérité">
              <select value={form.severity} onChange={e => set('severity', e.target.value)} required className={selectCls}>
                <option value="">Sélectionner...</option>
                {SEVERITY_LEVELS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
              {sevConfig && (
                <div className={`mt-2 px-2 py-1 rounded text-xs font-medium inline-flex items-center gap-1 ${sevConfig.bg} ${sevConfig.color} border ${sevConfig.border}`}>
                  <Zap className="w-3 h-3"/> {sevConfig.label}
                </div>
              )}
            </Field>
            <Field label="Région">
              <select value={form.region} onChange={e => { set('region', e.target.value); set('city',''); }} required className={selectCls}>
                <option value="">Sélectionner...</option>
                {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Ville">
              <select value={form.city} onChange={e => set('city', e.target.value)} required disabled={!form.region} className={selectCls + (form.region ? '' : ' opacity-40')}>
                <option value="">Sélectionner la région d'abord...</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Localisation précise" required={false} full>
              <input type="text" value={form.location_detail} onChange={e => set('location_detail', e.target.value)}
                placeholder="Quartier, rue, bâtiment, point de repère..." className={inputCls}/>
            </Field>
          </SECTION>

          {/* ── Section 2: Reporter & Assignment ── */}
          <SECTION title="Officier rapporteur & Assignation" icon={User}>
            <Field label="Nom de l'officier rapporteur">
              <input type="text" value={form.reported_by} onChange={e => set('reported_by', e.target.value)} required className={inputCls}/>
            </Field>
            <Field label="ID Officier">
              <input type="text" value={form.officer_id} readOnly className={inputCls + ' opacity-60 cursor-not-allowed'}/>
            </Field>
            <Field label="Grade / Rôle">
              <input type="text" value={form.reporter_role} onChange={e => set('reporter_role', e.target.value)} required className={inputCls}/>
            </Field>
            <Field label="Téléphone de contact" required={false}>
              <input type="tel" value={form.reporter_phone} onChange={e => set('reporter_phone', e.target.value)}
                placeholder="+237 6XX XXX XXX" className={inputCls}/>
            </Field>
            <Field label="Commissariat assigné">
              <select value={form.assigned_precinct} onChange={e => set('assigned_precinct', e.target.value)} required className={selectCls}>
                <option value="">Sélectionner...</option>
                {PRECINCTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </Field>
            <Field label="Email officier assigné" required={false}>
              <input type="email" value={form.assigned_email} onChange={e => set('assigned_email', e.target.value)}
                placeholder="prenom.nom@police.cm" className={inputCls}/>
            </Field>
          </SECTION>

          {/* ── Section 3: Incident Details ── */}
          <SECTION title="Détails de l'incident" icon={FileText}>
            <Field label="Description complète" full>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} required rows={4}
                placeholder="Décrivez l'incident en détail : circonstances, chronologie des faits, personnes impliquées, dommages constatés..."
                className={inputCls + ' resize-none'}/>
            </Field>
            <Field label="Nombre de victimes" required={false}>
              <input type="number" min="0" value={form.victims_count} onChange={e => set('victims_count', e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Nombre de suspects" required={false}>
              <input type="number" min="0" value={form.suspects_count} onChange={e => set('suspects_count', e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Description des suspects" required={false} full>
              <input type="text" value={form.suspects_description} onChange={e => set('suspects_description', e.target.value)}
                placeholder="Signalement physique, vêtements, véhicule utilisé..." className={inputCls}/>
            </Field>
            <Field label="Armes impliquées" required={false}>
              <input type="text" value={form.weapons_involved} onChange={e => set('weapons_involved', e.target.value)}
                placeholder="Type d'arme(s) si applicable" className={inputCls}/>
            </Field>
            <Field label="Dommages matériels" required={false}>
              <input type="text" value={form.property_damage} onChange={e => set('property_damage', e.target.value)}
                placeholder="Nature et estimation des dégâts" className={inputCls}/>
            </Field>
          </SECTION>

          {/* ── Section 4: Response ── */}
          <SECTION title="Réponse opérationnelle" icon={Shield}>
            <Field label="Actions immédiates entreprises" full>
              <textarea value={form.immediate_actions} onChange={e => set('immediate_actions', e.target.value)} rows={3}
                placeholder="Périmètre de sécurité, unités déployées, premiers soins, évacuation..."
                className={inputCls + ' resize-none'}/>
            </Field>
            <Field label="Unités déployées" required={false}>
              <input type="text" value={form.units_deployed} onChange={e => set('units_deployed', e.target.value)}
                placeholder="Noms / numéros des unités engagées" className={inputCls}/>
            </Field>
            <Field label="Temps de réponse (minutes)" required={false}>
              <input type="number" min="0" value={form.response_time_minutes} onChange={e => set('response_time_minutes', e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Renforts demandés" required={false}>
              <input type="text" value={form.backup_requested} onChange={e => set('backup_requested', e.target.value)}
                placeholder="Type de renforts demandés si applicable" className={inputCls}/>
            </Field>
          </SECTION>

          {/* ── Section 5: Status & Approval ── */}
          <SECTION title="Statut & Validation" icon={Clock}>
            <Field label="Statut de l'incident">
              <select value={form.status} onChange={e => set('status', e.target.value)} required className={selectCls}>
                {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Temps de traitement (heures)" required={false}>
              <input type="number" min="0" step="0.1" value={form.time_to_complete} onChange={e => set('time_to_complete', e.target.value)} className={inputCls}/>
            </Field>
            <Field label="Approuvé par" required={false}>
              <input type="text" value={form.approved_by} onChange={e => set('approved_by', e.target.value)}
                placeholder="Nom du superviseur / commissaire" className={inputCls}/>
            </Field>
          </SECTION>

          {/* ── Section 6: Evidence & Notes ── */}
          <SECTION title="Preuves & Notes complémentaires" icon={Camera}>
            {/* Photo upload */}
            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-blue-300 mb-1.5">Photo / Preuve visuelle</label>
              <div className="border-2 border-dashed border-blue-800/60 rounded-lg p-5 text-center bg-slate-700/30">
                {photoPreview ? (
                  <div className="relative inline-block">
                    <img src={photoPreview} alt="preview" className="max-h-44 rounded-lg mx-auto"/>
                    <button type="button" onClick={() => { setPhoto(null); setPhotoPreview(''); set('photo_url',''); }}
                      className="absolute top-1 right-1 p-1 bg-red-500 rounded-full">
                      <X className="w-3 h-3"/>
                    </button>
                  </div>
                ) : (
                  <>
                    <Camera className="w-8 h-8 text-blue-400/60 mx-auto mb-2"/>
                    <p className="text-xs text-slate-400 mb-2">PNG, JPEG, WEBP · max 10MB</p>
                    <input type="file" id="photo-upload" accept="image/png,image/jpeg,image/webp"
                      onChange={e => e.target.files?.[0] && handlePhoto(e.target.files[0])} className="hidden"/>
                    <label htmlFor="photo-upload"
                      className="px-4 py-2 bg-slate-600 hover:bg-slate-500 rounded-lg cursor-pointer text-xs text-white transition-colors inline-flex items-center gap-2">
                      <Upload className="w-3.5 h-3.5"/> Sélectionner une photo
                    </label>
                  </>
                )}
              </div>
            </div>
            <Field label="Nom du témoin" required={false}>
              <input type="text" value={form.witness_name} onChange={e => set('witness_name', e.target.value)}
                placeholder="Nom complet du témoin principal" className={inputCls}/>
            </Field>
            <Field label="Contact du témoin" required={false}>
              <input type="tel" value={form.witness_contact} onChange={e => set('witness_contact', e.target.value)}
                placeholder="+237 6XX XXX XXX" className={inputCls}/>
            </Field>
            <Field label="Notes complémentaires" required={false} full>
              <textarea value={form.additional_notes} onChange={e => set('additional_notes', e.target.value)} rows={3}
                placeholder="Toute information utile non couverte par les sections précédentes..."
                className={inputCls + ' resize-none'}/>
            </Field>
          </SECTION>

          {/* Hidden auto fields */}
          <div className="bg-slate-700/40 rounded-lg p-3 text-xs text-slate-400 flex flex-wrap gap-4 border border-blue-900/30">
            <span>ID: <span className="font-mono text-blue-300">{form.incident_id}</span></span>
            <span>Officier: <span className="font-mono text-blue-300">{form.officer_id}</span></span>
            <span>Créé: <span className="font-mono text-blue-300">{form.created_at}</span></span>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 flex-wrap gap-3">
            <p className="text-xs text-slate-400">* Champs obligatoires · Rapport horodaté et géolocalisé automatiquement</p>
            <button type="submit" disabled={loading}
              className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-gold-500 to-gold-600 hover:from-gold-600 hover:to-gold-700 text-navy-900 font-bold text-sm rounded-lg transition-all disabled:opacity-50 shadow-lg shadow-gold-500/20">
              {loading
                ? <><span className="animate-spin border-2 border-white/30 border-t-white rounded-full w-4 h-4"/> Envoi en cours...</>
                : <><Send className="w-4 h-4"/> Soumettre le rapport d'incident</>
              }
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
